import type { ReactNode } from "react";

import { ColocaIcon } from "@/shared/components/ColocaIcon";
import { useTranslations } from "@/shared/lang/useTranslations";

export interface WhatsAppBubbleProps {
  /** Sender line inside the bubble. The current templates have none, so it is omitted for them. */
  header?: string;
  body: ReactNode;
  /** The template's footer line, in grey under the body. */
  footer?: string;
  time?: string;
  /** Who receives it, rendered under the bubble. Omit when the recipient is the point of the card itself. */
  recipientHint?: string;
}

function DoubleTick() {
  return (
    <svg width="14" height="10" viewBox="0 0 16 10" fill="none" aria-hidden="true">
      <path
        d="M1 5.5 4 8.5 11 1"
        stroke="#34B7F1"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 5.5 8 8.5 15 1"
        stroke="#34B7F1"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function VerifiedCheck() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="7" fill="#25D366" />
      <path
        d="M5 8.5 7 10.5 11.5 5.5"
        stroke="#fff"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Replica of a WhatsApp chat bubble, used to preview the message a recipient will actually receive.
 *
 * It renders WhatsApp's own palette as literals rather than our tokens on purpose: the point is to show the
 * message as it lands in that app, so it must look the same in light and dark mode. Nothing here is a Coloca
 * surface — treating it as one would make the preview lie.
 */
export function WhatsAppBubble({ header, body, footer, time = "14:30", recipientHint }: WhatsAppBubbleProps) {
  const { t } = useTranslations("notificationPreferences");

  return (
    <div
      className="relative w-full"
      style={{
        background: "#ECE5DD",
        backgroundImage: "radial-gradient(rgba(0,0,0,0.04) 1px, transparent 1px)",
        backgroundSize: "12px 12px",
        padding: 14,
      }}
    >
      <div
        className="mb-2 text-center font-mono uppercase tracking-[0.08em]"
        style={{ fontSize: 9, color: "rgba(0,0,0,0.45)" }}
      >
        {t("preview_label")} · {t("preview_language_note")}
      </div>

      <div
        className="mr-auto"
        style={{
          background: "#fff",
          borderRadius: "10px 10px 10px 2px",
          padding: "10px 12px",
          maxWidth: 280,
          boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
          fontSize: 12,
          lineHeight: 1.5,
          color: "#111b21",
        }}
      >
        {header && (
          <div
            className="mb-1.5 flex items-center gap-1.5 border-b pb-1.5"
            style={{ borderColor: "rgba(0,0,0,0.08)" }}
          >
            <div
              className="grid place-items-center"
              style={{
                width: 20,
                height: 20,
                borderRadius: 5,
                background: "#00735C",
                padding: 3,
              }}
              aria-hidden
            >
              <ColocaIcon variant="white" size="14" />
            </div>
            <span style={{ fontSize: 10.5, fontWeight: 600, color: "#075E54" }}>{header}</span>
            <span className="ml-auto" title={t("preview_verified_title")}>
              <VerifiedCheck />
            </span>
          </div>
        )}

        <div>{body}</div>

        {footer && (
          <div className="mt-1.5" style={{ fontSize: 10.5, color: "rgba(0,0,0,0.45)" }}>
            {footer}
          </div>
        )}

        <div
          className="mt-1.5 flex items-center justify-end gap-1 font-mono"
          style={{ fontSize: 10, color: "rgba(0,0,0,0.45)" }}
        >
          <span>{time}</span>
          <DoubleTick />
        </div>
      </div>

      {recipientHint && (
        <div
          className="mt-2 text-center font-mono"
          style={{
            fontSize: 9,
            letterSpacing: "0.04em",
            color: "rgba(0,0,0,0.45)",
          }}
        >
          ↳ {recipientHint}
        </div>
      )}
    </div>
  );
}
